import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, MailCheck } from "lucide-react";

export default function VerifyListing() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const sentEmail = params.get("sent");

  const [preview, setPreview] = useState(null); // null=loading, false=error, obj=ok
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/listings/verify/${token}`)
      .then((r) => setPreview(r.data))
      .catch((e) => {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "Lien invalide");
        setPreview(false);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== password2) { setErr("Les mots de passe ne correspondent pas"); return; }
    if (password.length < 6) { setErr("6 caractères minimum"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/listings/verify", { token, password });
      await refresh();
      toast.success("Annonce validée — en attente de publication par notre équipe");
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Erreur");
      setSubmitting(false);
    }
  };

  // No token → arrived after submission ("email sent" screen)
  if (!token) {
    return (
      <div className="min-h-[70vh] px-6 md:px-12 lg:px-16 py-16 flex items-center justify-center">
        <div className="w-full max-w-lg border border-amber-500/30 bg-zinc-950 p-10 text-center">
          <MailCheck size={48} className="text-amber-500 mx-auto mb-6" />
          <div className="label-mono text-amber-500 mb-3">§ ANNONCE ENREGISTRÉE</div>
          <h1 className="font-display text-3xl mb-4">Vérifiez votre boîte mail</h1>
          <p className="text-zinc-300 leading-relaxed mb-2">
            Nous venons d'envoyer un email de confirmation à <br />
            <strong className="text-amber-500" data-testid="sent-email-value">{sentEmail}</strong>
          </p>
          <p className="text-sm text-zinc-500 leading-relaxed mb-8">
            Cliquez sur le lien reçu pour créer votre mot de passe et finaliser votre annonce. Le lien expire dans 48 h.
          </p>
          <div className="text-xs text-zinc-500 label-mono">
            Pas reçu ? Vérifiez vos spams ou <Link to="/contact" className="text-amber-500">contactez-nous</Link>
          </div>
        </div>
      </div>
    );
  }

  if (preview === null) return <div className="p-16 label-mono text-zinc-400">Vérification du lien…</div>;

  if (preview === false) {
    return (
      <div className="min-h-[70vh] px-6 md:px-12 lg:px-16 py-16 flex items-center justify-center">
        <div className="w-full max-w-lg border border-red-500/30 bg-zinc-950 p-10 text-center">
          <div className="label-mono text-red-400 mb-3">§ ERREUR</div>
          <h1 className="font-display text-3xl mb-4">Lien invalide ou expiré</h1>
          <p className="text-zinc-400 mb-8" data-testid="verify-error">{err}</p>
          <Link to="/deposer" className="inline-block bg-amber-500 text-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-amber-400">
            Redéposer l'annonce
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] px-6 md:px-12 lg:px-16 py-16 flex items-center justify-center">
      <div className="w-full max-w-xl border border-zinc-800 bg-zinc-950 p-10">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle2 size={22} className="text-emerald-400" />
          <div className="label-mono text-emerald-400">§ EMAIL VÉRIFIÉ</div>
        </div>
        <h1 className="font-display text-3xl mb-6">Finalisez votre annonce</h1>

        <div className="border border-zinc-800 bg-zinc-900/50 p-4 mb-8">
          <div className="label-mono mb-2">Récapitulatif</div>
          <div className="font-display text-xl mb-1">{preview.listing.title}</div>
          <div className="label-mono text-zinc-500">
            {preview.listing.city} · {preview.listing.surface_exploitable}m² · Score {preview.listing.score}/100
          </div>
          <div className="label-mono text-zinc-500 mt-2">Email : <span className="text-amber-500">{preview.email}</span></div>
        </div>

        {preview.account_exists ? (
          <p className="text-sm text-amber-500 mb-6">Un compte existe déjà avec cet email — entrez votre mot de passe pour rattacher l'annonce.</p>
        ) : (
          <p className="text-sm text-zinc-400 mb-6">Choisissez un mot de passe pour créer votre compte propriétaire et soumettre l'annonce à validation.</p>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
          <div>
            <div className="label-mono mb-2">{preview.account_exists ? "Votre mot de passe" : "Mot de passe (min. 6)"}</div>
            <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="verify-password" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          {!preview.account_exists && (
            <div>
              <div className="label-mono mb-2">Confirmer le mot de passe</div>
              <input required type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} data-testid="verify-password2" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          )}
          {err && <div className="text-sm text-red-400 label-mono">{err}</div>}
          <button type="submit" disabled={submitting} data-testid="verify-submit" className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black py-3 text-xs uppercase tracking-widest">
            {submitting ? "Validation…" : (preview.account_exists ? "Rattacher l'annonce" : "Créer mon compte & soumettre")}
          </button>
        </form>
      </div>
    </div>
  );
}
